import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import "./App.css";
import { UserProvider } from "./context/UserContext";
import Layout from "./components/common/Layout/Layout";
import ScrollToTop from "./utils/ScrollToTop";
import LocalBusinessSEO from "./components/common/LocalBusinessSEO/LocalBusinessSEO";

import Home from "./components/pages/Home/HomePage";
import About from "./components/pages/About/AboutUs";
import HowItWorks from "./components/pages/HowItWorks/HowItWorks";
import Blog from "./components/blog/Blog/Blog";
import BlogPost from "./components/blog/BlogPost/BlogPost";
import DownloadPage from "./components/pages/Download/DownloadPage";
import NotFound from "./components/pages/NotFound/NotFoundPage";
import Careers from "./components/pages/Careers/Careers";
import Contact from "./components/pages/Contact/Contact";
import ResetPasswordScreen from "./components/auth/ResetPassword/ResetPasswordScreen";
import RequestAccountDeletion from "./components/legal/AccountDeletion/DeleteRequestForm";
import ConfirmAccountDeletion from "./components/legal/AccountDeletion/ConfirmDeletePage";
import PrivacyPolicy from "./components/legal/PrivacyPolicy/PrivacyPolicy";
import TermsOfService from "./components/legal/TermsOfService/TermsOfService";
import LegalInquiries from "./components/legal/LegalInquiries/LegalInquiries";
import Partnerships from "./components/business/Partnerships/Partnerships";
import PressMedia from "./components/business/PressMedia/PressMedia";
import HelpCenter from "./components/business/HelpCenter/HelpCenter";
import PaymentSuccess from "./components/auth/PaymentSuccess/PaymentSuccess";
import Unsubscribe from "./components/legal/Unsubscribe/Unsubscribe";
import PickupSoccer from "./components/pages/PickupSoccer/PickupSoccer";
import DevDocs from "./components/internal/DevDocs/DevDocs";
import AdminDashboard from "./components/internal/AdminDashboard/AdminDashboard";
import UTMRedirect from "./components/common/UTMRedirect/UTMRedirect";
import InternalLogin from "./components/auth/InternalLogin/InternalLogin";
import AdminLogin from "./components/auth/AdminLogin/AdminLogin";
import ProtectedRoute from "./components/auth/ProtectedRoute/ProtectedRoute";
import SessionTimeout from "./components/auth/SessionTimeout/SessionTimeout";

function App() {
  return (
    <HelmetProvider>
      <UserProvider>
        <SessionTimeout />
        <Router>
          <ScrollToTop />
          <LocalBusinessSEO />
          <Layout>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/about" element={<About />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/pickup-soccer" element={<PickupSoccer />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:id" element={<BlogPost />} />
              <Route path="/download" element={<DownloadPage />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/reset-password" element={<ResetPasswordScreen />} />
              <Route
                path="/delete-account"
                element={<RequestAccountDeletion />}
              />
              <Route
                path="/confirm-delete"
                element={<ConfirmAccountDeletion />}
              />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/legal-inquiries" element={<LegalInquiries />} />
              <Route path="/partnerships" element={<Partnerships />} />
              <Route path="/press-media" element={<PressMedia />} />
              <Route path="/help-center" element={<HelpCenter />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/unsubscribe" element={<Unsubscribe />} />
              <Route path="/careers" element={<Careers />} />

              {/* Authentication Routes */}
              <Route path="/internal-login" element={<InternalLogin />} />
              <Route path="/admin-login" element={<AdminLogin />} />

              {/* Protected Employee Routes (Admins can also access) */}
              <Route
                path="/internal-dev-setup-d8f3a2c1"
                element={
                  <ProtectedRoute
                    requiredRole={["EMPLOYEE", "ADMIN"]}
                    redirectTo="/internal-login"
                  >
                    <DevDocs />
                  </ProtectedRoute>
                }
              />

              {/* Protected Admin Routes */}
              <Route
                path="/admin-dashboard"
                element={
                  <ProtectedRoute
                    requiredRole="ADMIN"
                    redirectTo="/admin-login"
                  >
                    <AdminDashboard />
                  </ProtectedRoute>
                }
              />

              {/* UTM Redirect Links - /download/... format */}
              <Route
                path="/download/reddit"
                element={
                  <UTMRedirect
                    source="reddit"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/tt"
                element={
                  <UTMRedirect
                    source="tiktok"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/tiktok"
                element={
                  <UTMRedirect
                    source="tiktok"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/fb"
                element={
                  <UTMRedirect
                    source="facebook"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/facebook"
                element={
                  <UTMRedirect
                    source="facebook"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/craigslist"
                element={
                  <UTMRedirect
                    source="craigslist"
                    medium="listing"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/ig"
                element={
                  <UTMRedirect
                    source="instagram"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/instagram"
                element={
                  <UTMRedirect
                    source="instagram"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/tw"
                element={
                  <UTMRedirect
                    source="twitter"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/twitter"
                element={
                  <UTMRedirect
                    source="twitter"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/x"
                element={
                  <UTMRedirect
                    source="twitter"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/download/qr"
                element={
                  <UTMRedirect
                    source="qr"
                    medium="poster"
                    campaign="vadnais_launch"
                  />
                }
              />

              {/* Short form UTM Redirect Links */}
              <Route
                path="/reddit"
                element={
                  <UTMRedirect
                    source="reddit"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/tt"
                element={
                  <UTMRedirect
                    source="tiktok"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/tiktok"
                element={
                  <UTMRedirect
                    source="tiktok"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/fb"
                element={
                  <UTMRedirect
                    source="facebook"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/facebook"
                element={
                  <UTMRedirect
                    source="facebook"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/craigslist"
                element={
                  <UTMRedirect
                    source="craigslist"
                    medium="listing"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/ig"
                element={
                  <UTMRedirect
                    source="instagram"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/instagram"
                element={
                  <UTMRedirect
                    source="instagram"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/tw"
                element={
                  <UTMRedirect
                    source="twitter"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/twitter"
                element={
                  <UTMRedirect
                    source="twitter"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/x"
                element={
                  <UTMRedirect
                    source="twitter"
                    medium="social"
                    campaign="oct_launch"
                  />
                }
              />
              <Route
                path="/qr"
                element={
                  <UTMRedirect
                    source="qr"
                    medium="poster"
                    campaign="vadnais_launch"
                  />
                }
              />

              <Route path="*" element={<NotFound />} />
            </Routes>
          </Layout>
        </Router>
      </UserProvider>
    </HelmetProvider>
  );
}

export default App;
